/* ===========================================================================
   Leitura e escrita de campos monetários.

   Vive num pacote compartilhado porque dois aplicativos precisam concordar
   sobre o que "39.90" significa: o painel (preço de produto, taxa de entrega)
   e o backoffice (preço do plano). Duplicar esta regra foi como nasceu o bug
   de `39.90` virar `3990` na página de preços — um parser próprio, escrito
   do zero, sem a história que os comentários abaixo guardam.

   Preço é gravado em `numeric(10,2)`: um valor com três casas era aceito no
   input, enviado no autosave e arredondado no banco (19.999 virava 20.00) sem
   nenhum aviso. Aqui o corte acontece no cliente, então o valor exibido é
   sempre o valor salvo.

   A formatação vive junto do parse porque os dois precisam concordar sobre o
   separador. Os campos eram `<input type="number">` com `value` numérico, que
   o navegador serializa por `Number.prototype.toString`: `12.5` aparecia como
   `12,5` enquanto a lista do cardápio e a prévia do cliente mostravam
   `R$ 9,90` — o mesmo valor com duas grafias no mesmo produto.
   ========================================================================== */

export const PRICE_DECIMALS = 2;

export interface MoneyInputResult {
  value: number;
  /** `true` quando casas decimais além do limite foram descartadas. */
  trimmed: boolean;
}

/** Só casa com decimal simples ("19.999"); notação científica passa direto. */
const PLAIN_DECIMAL = /^-?\d*\.(\d+)$/;

/**
 * Converte o texto digitado em valor com no máximo duas casas.
 *
 * Aceita vírgula e ponto: o campo é de texto para poder exibir `12,50`, então
 * o usuário brasileiro digita vírgula e o valor colado de outra tela vem com
 * ponto. Separador de milhar é descartado.
 *
 * O corte é feito na string em vez de `Math.trunc(value * 100) / 100` porque a
 * multiplicação erra em valores comuns: `0.29 * 100` é 28.999999999999996 e
 * truncaria para 0.28.
 */
export function parseMoneyInput(raw: string): MoneyInputResult {
  const text = normalizeSeparators(raw);
  const parsed = Number(text);
  if (!Number.isFinite(parsed)) return { value: 0, trimmed: false };

  const decimals = PLAIN_DECIMAL.exec(text)?.[1];
  if (!decimals || decimals.length <= PRICE_DECIMALS) {
    return { value: parsed, trimmed: false };
  }

  const cut = Number(text.slice(0, text.indexOf('.') + 1 + PRICE_DECIMALS));
  return Number.isFinite(cut)
    ? { value: cut, trimmed: true }
    : { value: parsed, trimmed: false };
}

/**
 * Texto de edição do campo: `12,50`. Sem símbolo de moeda — o `R$` é desenhado
 * pelo próprio campo, fora do input.
 */
export function formatMoneyInput(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return value.toFixed(PRICE_DECIMALS).replace('.', ',');
}

/** Texto de leitura: `R$ 12,50`. Usado onde o valor não é editável. */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/** `1.234,5` e `1234.5` chegam os dois como `1234.5`. */
function normalizeSeparators(raw: string): string {
  const text = raw.trim();
  if (!text) return '';

  const lastComma = text.lastIndexOf(',');
  const lastDot = text.lastIndexOf('.');
  // O separador decimal é o último a aparecer; o outro é milhar.
  const decimalMark = lastComma > lastDot ? ',' : '.';
  const thousandMark = decimalMark === ',' ? '.' : ',';

  return text.split(thousandMark).join('').replace(decimalMark, '.');
}
